import { Controller, useFormContext } from 'react-hook-form'
import { InstalledBotRow } from '@components/InstalledBotRow/InstalledBotRow'
import { SpaceAddressFromSpaceId } from '@towns-protocol/web3'
import { Box, Checkbox, Stack, Text } from '@ui'
import { type Address, useBotData, useInstalledApps } from 'use-towns-client'

import type { CreateChannelFormSchema } from './createChannelFormSchema'

type BotRowWithCheckboxProps = {
    appAddress: Address
    clientAddress: string
}

const BotRowWithCheckbox = ({ appAddress, clientAddress }: BotRowWithCheckboxProps) => {
    const { data: bot } = useBotData(appAddress)
    const { control } = useFormContext<CreateChannelFormSchema>()

    if (!bot) {
        return null
    }

    return (
        <Box background="level2" rounded="sm">
            <Controller
                name="botClientAddresses"
                control={control}
                render={({ field }) => {
                    const isChecked = field.value?.includes(clientAddress) ?? false
                    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        const checked = e.target.checked
                        const current = field.value || []
                        if (checked) {
                            field.onChange([...current, clientAddress])
                        } else {
                            field.onChange(current.filter((addr) => addr !== clientAddress))
                        }
                    }
                    return (
                        <Checkbox
                            name={`bot-${bot.clientAddress}`}
                            label={<InstalledBotRow disableClick appAddress={appAddress} />}
                            checked={isChecked}
                            width="100%"
                            labelLast={false}
                            onChange={handleChange}
                        />
                    )
                }}
            />
        </Box>
    )
}

type BotSelectionSectionProps = {
    spaceId: string
}

export const BotSelectionSection = ({ spaceId }: BotSelectionSectionProps) => {
    const { data: installedApps } = useInstalledApps(
        spaceId ? SpaceAddressFromSpaceId(spaceId) : undefined,
    )

    if (!installedApps || installedApps.size === 0) {
        return null
    }

    const botEntries = Array.from(installedApps.entries())

    return (
        <Box paddingTop="md" paddingBottom="sm">
            <Stack gap>
                <Stack gap="sm">
                    <Text>Add Bots</Text>
                    <Text color="gray2" size="sm">
                        Add bots to grant access and apply their permissions to this channel.
                    </Text>
                </Stack>
                <Stack padding gap="sm" background="level2" rounded="sm">
                    {botEntries.map(([clientAddress, app]) => (
                        <BotRowWithCheckbox
                            key={clientAddress}
                            appAddress={app.appAddress}
                            clientAddress={clientAddress}
                        />
                    ))}
                </Stack>
            </Stack>
        </Box>
    )
}
